/* eslint-disable */
import * as React from 'react';
import type { Preceptor } from '@/types/preceptor';
import { Card } from '@/components/ui/card';
import Link from 'next/link';
import { deletePreceptor } from '@/services/preceptor-service';

interface Props {
  preceptor: Preceptor;
  index?: number;
  selected?: boolean;
  distanceMiles?: number;
  isAdmin?: boolean;
  onRemoved?: (preceptorId: number | string) => void;
  selectedSpecialtyId?: number;
  /** When 'img', the View Details link points at the IMG rotation flow. */
  track?: 'img';
}

export function PreceptorResultCard({ preceptor, index, selected, distanceMiles, isAdmin, onRemoved, selectedSpecialtyId, track }: Props) {
  const [confirmingRemove, setConfirmingRemove] = React.useState(false);
  const [isRemoving, setIsRemoving] = React.useState(false);
  const [notesOpen, setNotesOpen] = React.useState(false);
  const schedulingNotes = preceptor.schedulingNotes?.filter(Boolean) ?? [];

  async function handleRemove() {
    if (!preceptor.id) return;
    setIsRemoving(true);
    try {
      await deletePreceptor(preceptor.id);
      onRemoved?.(preceptor.id);
    } catch (err) {
      console.error('Failed to remove preceptor', err);
    } finally {
      setIsRemoving(false);
      setConfirmingRemove(false);
    }
  }
  // Format location string
  const location = [preceptor.city, preceptor.state].filter(Boolean).join(', ');
  
  // Get specialties for display, prioritizing selected specialty
  const rawSpecialties = preceptor.specialties?.filter(s => s.name) || [];
  const specialties = React.useMemo(() => {
    if (!selectedSpecialtyId || !rawSpecialties.length) return rawSpecialties;
    const selectedIndex = rawSpecialties.findIndex(s => Number(s.id) === selectedSpecialtyId);
    if (selectedIndex <= 0) return rawSpecialties; // Already first or not found
    const reordered = [...rawSpecialties];
    const [selected] = reordered.splice(selectedIndex, 1);
    reordered.unshift(selected);
    return reordered;
  }, [rawSpecialties, selectedSpecialtyId]);
  const primarySpecialty = specialties[0]?.name || preceptor.specialty || '';
  const additionalCount = specialties.length > 1 ? specialties.length - 1 : 0;
  
  // Format seasons (e.g., "Spring 2026, Summer 2026")
  const seasonsText = preceptor.seasonsAvailable?.join(', ') || '';

  // Format modality (Telehealth / In-Person)
  const modality = (() => {
    const tele = preceptor.accepts_tele;
    const inPerson = preceptor.accepts_in_person;
    if (tele && inPerson) return 'Telehealth & In-Person';
    if (tele) return 'Telehealth';
    if (inPerson) return 'In-Person';
    return '';
  })();

  return (
    <Card className={`group relative overflow-hidden border rounded-xl bg-white !p-0 !gap-0 transition-all duration-200 ${selected ? 'border-blue-500 ring-1 ring-blue-500/20 shadow-md' : 'border-gray-200 hover:border-gray-300 hover:shadow-sm'}`}>

      {isAdmin ? (
        <button
          type="button"
          onClick={() => setConfirmingRemove(true)}
          className="absolute top-2 right-2 z-10 text-xs font-medium text-white/90 hover:text-white bg-red-600/80 hover:bg-red-700 px-2 py-1 rounded-md"
        >
          Remove
        </button>
      ) : null}

      {confirmingRemove ? (
        <div role="dialog" aria-modal="true" className="absolute inset-0 z-20 flex items-center justify-center bg-white/95 rounded-xl border border-red-200 p-4">
          <div className="space-y-3 text-center">
            <p className="text-sm font-medium">Remove this preceptor?</p>
            <p className="text-xs text-[var(--brand-muted,#6b7280)]">They&apos;ll be hidden from rotations and search. You can restore them later from the Removed Preceptors page.</p>
            <div className="flex justify-center gap-2">
              <button
                type="button"
                onClick={() => setConfirmingRemove(false)}
                className="text-xs font-medium px-3 py-1.5 border rounded-md"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleRemove}
                disabled={isRemoving}
                className="text-xs font-medium px-3 py-1.5 bg-red-600 text-white rounded-md disabled:opacity-50"
              >
                {isRemoving ? 'Removing…' : 'Remove'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/* Header */}
      <div className="px-4 py-3 bg-gradient-to-r from-blue-600 to-cyan-600">
        <p className="text-blue-100 text-sm font-medium mb-0.5">Rotation Code</p>
        <span className="text-white text-sm font-bold tracking-wide">{preceptor.rotationCode || '—'}</span>
      </div>

      {/* Info Grid */}
      <div className="p-4 space-y-2.5">
        {/* Credential */}
        {preceptor.titles && preceptor.titles.length > 0 && (
          <div className="flex items-start gap-2.5">
            <div className="flex-shrink-0 w-4 h-4 mt-0.5 text-blue-500">
              <svg fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 9h3.75M15 12h3.75M15 15h3.75M4.5 19.5h15a2.25 2.25 0 002.25-2.25V6.75A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25v10.5A2.25 2.25 0 004.5 19.5zm6-10.125a1.875 1.875 0 11-3.75 0 1.875 1.875 0 013.75 0zm1.294 6.336a6.721 6.721 0 01-3.17.789 6.721 6.721 0 01-3.168-.789 3.376 3.376 0 016.338 0z" />
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs text-gray-400 font-medium">Credential</p>
              <p className="text-xs font-medium text-gray-800">{preceptor.titles.join(', ')}</p>
            </div>
          </div>
        )}

        {/* Specialty */}
        {primarySpecialty && (
          <div className="flex items-start gap-2.5">
            <div className="flex-shrink-0 w-4 h-4 mt-0.5 text-blue-500">
              <svg fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9.75 3.104v5.714a2.25 2.25 0 01-.659 1.591L5 14.5M9.75 3.104c-.251.023-.501.05-.75.082m.75-.082a24.301 24.301 0 014.5 0m0 0v5.714c0 .597.237 1.17.659 1.591L19 14.5M14.25 3.104c.251.023.501.05.75.082M19 14.5v4.25c0 .621-.504 1.125-1.125 1.125H6.125A1.125 1.125 0 015 18.75V14.5" />
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs text-gray-400 font-medium">{additionalCount > 0 ? 'Specialties' : 'Specialty'}</p>
              <p className="text-xs font-medium text-gray-800">{specialties.map(s => s.name).join(', ')}</p>
            </div>
          </div>
        )}

        {/* Location */}
        {location && (
          <div className="flex items-start gap-2.5">
            <div className="flex-shrink-0 w-4 h-4 mt-0.5 text-blue-500">
              <svg fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" />
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs text-gray-400 font-medium">Location</p>
              <p className="text-xs font-medium text-gray-800 truncate">{location}</p>
            </div>
          </div>
        )}

        {/* Rotation Setting */}
        {modality && (
          <div className="flex items-start gap-2.5">
            <div className="flex-shrink-0 w-4 h-4 mt-0.5 text-blue-500">
              <svg fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 17.25v1.007a3 3 0 01-.879 2.122L7.5 21h9l-.621-.621A3 3 0 0115 18.257V17.25m6-12V15a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 15V5.25m18 0A2.25 2.25 0 0018.75 3H5.25A2.25 2.25 0 003 5.25m18 0V12a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 12V5.25" />
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs text-gray-400 font-medium">Rotation Setting</p>
              <p className="text-xs font-medium text-gray-800 truncate">{modality}</p>
            </div>
          </div>
        )}

        {/* Seasons Available */}
        {seasonsText && (
          <div className="flex items-start gap-2.5">
            <div className="flex-shrink-0 w-4 h-4 mt-0.5 text-blue-500">
              <svg fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" />
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs text-gray-400 font-medium">Seasons Available</p>
              <p className="text-xs font-medium text-gray-800 line-clamp-1">{seasonsText}</p>
            </div>
          </div>
        )}

        {/* Distance */}
        {typeof distanceMiles === 'number' && (
          <div className="flex items-start gap-2.5">
            <div className="flex-shrink-0 w-4 h-4 mt-0.5 text-blue-500">
              <svg fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 6.75V15m6-6v8.25m.503 3.498l4.875-2.437c.381-.19.622-.58.622-1.006V4.82c0-.836-.88-1.38-1.628-1.006l-3.869 1.934c-.317.159-.69.159-1.006 0L9.503 3.252a1.125 1.125 0 00-1.006 0L3.622 5.689C3.24 5.88 3 6.27 3 6.695V19.18c0 .836.88 1.38 1.628 1.006l3.869-1.934c.317-.159.69-.159 1.006 0l4.994 2.497c.317.158.69.158 1.006 0z" />
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs text-gray-400 font-medium">Distance</p>
              <p className="text-xs font-medium text-gray-800">{distanceMiles.toFixed(1)} miles</p>
            </div>
          </div>
        )}
      </div>

      {/* Scheduling Notes (collapsible to keep the card compact) */}
      {schedulingNotes.length > 0 && (
        <div className="px-4 pb-1">
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setNotesOpen((o) => !o); }}
            aria-expanded={notesOpen}
            className="w-full flex items-center justify-between gap-2 rounded-md border border-amber-200 bg-amber-50 px-2.5 py-1.5 text-left hover:bg-amber-100/70 transition-colors"
          >
            <span className="flex items-center gap-1.5 text-[11px] font-semibold text-amber-800">
              <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l2.5 2.5M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Scheduling Notes ({schedulingNotes.length})
            </span>
            <svg className={`w-3.5 h-3.5 flex-shrink-0 text-amber-700 transition-transform duration-200 ${notesOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
            </svg>
          </button>
          {notesOpen && (
            <ul className="mt-1.5 space-y-1 rounded-md border border-amber-100 bg-amber-50/60 px-2.5 py-2">
              {schedulingNotes.map((note, idx) => (
                <li key={`${note}-${idx}`} className="flex items-start gap-1.5 text-[11px] leading-snug text-neutral-700">
                  <span className="mt-1 h-1 w-1 flex-shrink-0 rounded-full bg-amber-500" />
                  <span>{note}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Action */}
      <div className="px-4 pb-4 pt-2 mt-2 border-t border-gray-100">
        <Link
          href={preceptor.rotationCode
            ? `/rotations/${encodeURIComponent(preceptor.rotationCode)}${preceptor.id ? `?site=${encodeURIComponent(preceptor.id)}` : ''}${track === 'img' ? `${preceptor.id ? '&' : '?'}track=img` : ''}`
            : '#'}
          className={`cursor-pointer w-full inline-flex items-center justify-center gap-1.5 rounded-lg text-xs font-semibold px-4 py-2 transition-all
            ${preceptor.rotationCode ? 'bg-white text-blue-600 border border-blue-600 hover:bg-blue-50 active:scale-[0.98]' : 'bg-gray-100 text-gray-400 cursor-not-allowed'}`}
          aria-disabled={!preceptor.rotationCode}
        >
          <span>View Details</span>
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
          </svg>
        </Link>
      </div>
    </Card>
  );
}

export default PreceptorResultCard;
